/** @format */
import { useTranslation } from 'next-i18next';
import { useContext } from 'react';

import { getFormattedDateTime } from '@toucan/client/global/date';
import { MarketContext } from '@toucan/client/global/market';

import { useSecondaryCredit } from '../use-secondary-credit';
import {
    AuthorCreditEntry,
    UseAuthorCreditParameters,
    UseAuthorCreditReturn,
} from './config/types';

export type {
    AuthorCreditEntry,
    UseAuthorCreditParameters,
    UseAuthorCreditReturn,
};

export const useAuthorCredit = (): UseAuthorCreditReturn => {
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);
    const { getSecondaryCredit } = useSecondaryCredit();

    const formatDate = (value: string | null) =>
        value
            ? getFormattedDateTime(activeMarket.iso, value, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
              })
            : null;

    const getCredits = ({
        author,
        writtenAt,
        ...secondaryParameters
    }: UseAuthorCreditParameters): AuthorCreditEntry[] => {
        if (!author) return [];

        const credits: AuthorCreditEntry[] = [
            {
                person: author,
                date: formatDate(writtenAt),
                getText: (name) =>
                    t('Written by <author-link>{{author}}</author-link>', {
                        author: name,
                    }),
            },
        ];

        const secondaryCredit = getSecondaryCredit(secondaryParameters);

        if (secondaryCredit) {
            credits.push({
                ...secondaryCredit,
                date: formatDate(secondaryCredit.date),
            });
        }

        return credits;
    };

    return { getCredits };
};
